import * as types from 'Store/constants';
import { checkNotRunningHorses } from 'Utils/horseRacesNameMapper';
import EnvironmentManager from 'Utils/environmentManager';

const initialState = {
  isOpen: false,
  selectedInfos: {},
  bultenInfo: {},
  isBultenChanged: false,
  legs: [],
  lastAddedLeg: {},
  lastAddedHorses: [],
  isLoading: null,
  error: null,
  verify: {
    data: [],
    isLoading: null,
    error: null,
  },
  footerData: {
    multiplier: 1,
    fixedOddMultiplier: Number(EnvironmentManager.getMessageText({ key: 'v1.betslip_fixodd.minimum_multiplier' }) || 20),
    couponCount: 1,
    allVariation: false,
    system: [],
  },
  showSystemModal: false,
  play: { isLoading: false },
  isLoadedFromLS: false,
  isLoadedFromCoupon: {},
  combinedHorses: [],
  propositions: [],
  toastData: {
    show: false,
    content: null,
    buttonText: null,
    icon: null,
    onButtonClick: null,
    onHide: null,
    duration: 5000,
    justify: false
  }
};

// Yardımcı fonksiyonlar
const helpers = {
  checkIsChanged: (state, isHippodromeChanged) => {
    if (isHippodromeChanged) return true;

    const { selectedBet, selectedRaceNumber } = state.selectedInfos || {};
    const { bt, rt } = state.bultenInfo || {};

    return !!(
      (selectedBet?.id && selectedBet.id !== bt)
      || (selectedRaceNumber && selectedRaceNumber !== rt)
    );
  },

  updateLegs: (legs, index, newValue) => {
    const newLegs = [...legs];
    newLegs[index] = newValue;
    return newLegs;
  },

  updateFooterData: (state, key, value) => ({
    ...state,
    footerData: { ...state.footerData, [key]: value }
  }),

  updateVerifyState: (state, updates) => ({
    ...state,
    verify: { ...state.verify, ...updates }
  })
};

// Action handlers
const handlers = {
  [types.SET_HR_BETSLIP_FROM_LOCAL_STORAGE]: (state, { payload }) => ({
    ...(payload.horseRacingBetslip || initialState),
    isLoadedFromLS: true
  }),

  [types.CHANGE_BULTEN_RACENUMBER]: (state, { payload }) => {
    const newState = { ...state, bultenInfo: payload };
    const hippChanged = state.selectedInfos?.selectedHippodrome?.cardId !== payload?.hpdm;
    return {
      ...newState,
      isBultenChanged: helpers.checkIsChanged(newState, hippChanged)
    };
  },

  [types.SELECT_HORSE]: (state, { payload: { leg, horse, selectedInfos } }) => {
    const updatedLegs = helpers.updateLegs(
      state.legs,
      leg,
      [...(state.legs[leg] || []), horse]
    );

    return {
      ...state,
      lastAddedLeg: horse,
      legs: updatedLegs,
      selectedInfos,
      isBultenChanged: helpers.checkIsChanged({ ...state, selectedInfos })
    };
  },

  [types.REMOVE_HORSE]: (state, { payload: { leg, horse } }) => {
    const filteredHorses = (state.legs[leg] || [])
      .filter(e => e.uniqId !== horse.uniqId);

    return {
      ...state,
      legs: helpers.updateLegs(
        state.legs,
        leg,
        filteredHorses.length > 0 ? filteredHorses : null
      )
    };
  },

  [types.SELECT_ALL_HORSES]: (state, { payload: { leg, horses, selectedInfos } }) => ({
    ...state,
    legs: helpers.updateLegs(state.legs, leg, horses),
    lastAddedHorses: horses,
    selectedInfos,
    isBultenChanged: helpers.checkIsChanged({ ...state, selectedInfos })
  }),

  [types.REMOVE_ALL_HORSES]: (state, { payload: { leg } }) => ({
    ...state,
    legs: helpers.updateLegs(state.legs, leg, null)
  }),

  [types.VERIFY_TJK_COUPON_REQUEST]: state =>
    helpers.updateVerifyState(state, { isLoading: true }),

  [types.VERIFY_TJK_COUPON_SUCCESS]: (state, { payload }) =>
    helpers.updateVerifyState(state, {
      isLoading: false,
      error: null,
      data: payload
    }),

  [types.VERIFY_TJK_COUPON_FAILURE]: (state, { payload }) => ({
    ...state,
    legs: checkNotRunningHorses(state, payload),
    verify: { data: [], isLoading: false, error: payload }
  }),

  [types.SET_ORDERED_LEGS]: (state, { payload }) => ({
    ...state,
    legs: payload
  }),

  [types.SET_HR_BETSLIP_MULTIPLIER]: (state, { payload }) =>
    helpers.updateFooterData(state, 'multiplier', payload),

  [types.SET_HR_FIXED_ODD_MULTIPLIER]: (state, { payload }) =>
    helpers.updateFooterData(state, 'fixedOddMultiplier', payload),

  [types.SET_HR_BETSLIP_COUPON_COUNT]: (state, { payload }) =>
    helpers.updateFooterData(state, 'couponCount', payload),

  [types.SET_HR_BETSLIP_ALL_VARIATION]: (state, { payload }) =>
    helpers.updateFooterData(state, 'allVariation', payload),

  [types.SET_SYSTEM]: (state, { payload }) =>
    helpers.updateFooterData(state, 'system', payload),

  [types.RESET_HR_BETSLIP]: state => ({
    ...initialState,
    toastData: state.toastData,
    bultenInfo: { ...state.bultenInfo },
    isOpen: state.isOpen,
  }),

  [types.TOGGLE_HR_BETSLIP]: (state, { payload }) => ({
    ...state,
    isOpen: payload
  }),

  [types.TOGGLE_SYSTEM_MODAL]: (state, { payload }) => ({
    ...state,
    showSystemModal: payload
  }),

  [types.HORSE_RACING_WS_BETSLIP_UPDATE]: (state, { payload }) => ({
    ...state,
    legs: payload,
  }),

  [types.PLAY_TJK_COUPON_REQUEST]: state => ({
    ...state,
    play: { isLoading: true }
  }),

  [types.PLAY_TJK_COUPON_SUCCESS]: state => ({
    ...state,
    play: { isLoading: false }
  }),

  [types.PLAY_COUPON_FAILURE]: state => ({
    ...state,
    play: { isLoading: false }
  }),

  [types.SET_HR_BETSLIP_FROM_COUPON]: (state, { payload, isLoadedFromCouponConfig }) => {
    payload.footerData.system = state.footerData.system;
    return {
      ...initialState,
      ...state,
      ...payload,
      isLoadedFromCouponConfig,
      isOpen: false
    };
  },

  [types.CHANGE_ODD]: (state, { payload }) => {
    if (!payload?.propositionId) return state;

    const hasMatchingHorse = state.combinedHorses?.some(h =>
      h => h?.horse?.propositionInfo?.propositionId === payload.propositionId
    );

    if (!hasMatchingHorse) return state;

    const combinedHorses = [...state.combinedHorses];

    const horseIndex = combinedHorses.findIndex(h => h?.horse?.propositionInfo?.propositionId === payload.propositionId);

    if (horseIndex === -1) return state;

    const horse = { ...combinedHorses[horseIndex] };

    horse.propositionInfo = { ...payload };

    combinedHorses[horseIndex] = horse;

    return { ...state, combinedHorses };
  },

  [types.ADD_COMBINED_HORSE]: (state, { payload }) => {
    return { ...state, combinedHorses: [...state.combinedHorses, payload] };
  },

  [types.REMOVE_COMBINED_HORSE]: (state, { payload }) => {

    return { ...state, combinedHorses: [...state.combinedHorses.filter(e => payload?.uniqId ? (e?.horse?.uniqId !== payload?.uniqId) : e?.horse?.propositionInfo?.propositionId !== payload?.propositionInfo?.propositionId)] };
  },

  [types.REPLACE_COMBINED_HORSE]: (state, { payload }) => {

    return { ...state, combinedHorses: [...state.combinedHorses.filter(e => !(e?.hippodrome?.cardId === payload?.hippodrome?.cardId && e?.leg?.legNo === payload?.leg?.legNo && e?.bet?.id === payload?.bet?.id)), payload] };
  },

  [types.SET_BETSLIP_TOAST]: (state, { payload }) => {
    return {
      ...state,
      toastData: payload
    };
  },
  [types.ADD_PROPOSITION]: (state, { payload }) => {
    return { ...state, propositions: [...state.propositions, payload] };
  },

  [types.REMOVE_PROPOSITION]: (state, { payload }) => {
    return { ...state, propositions: [...state.propositions.filter(e => e?.proposition?.propositionId !== payload?.propositionId )] };
  },

  [types.REPLACE_PROPOSITION]: (state, { payload }) => {
    return { ...state, propositions: [payload] };
  },

};

export default function reducer (state = initialState, action) {
  const handler = handlers[action.type];

  return handler ? handler(state, action) : state;
}
